import { useCallback, useEffect, useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import type { CreatorsService } from '@domain/usecases/creators/CreatorsService';
import type { ContentItemModel } from '@domain/models';
import { PuzzleSortField } from '@domain/usecases/puzzles/PuzzleSortField';
import { useSort } from '@presentation/hooks/useSort';

export const useCreatorContents = (creatorId: string | undefined, firstPage: any, creatorService: CreatorsService) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();

	const { sort, handleSort } = useSort<PuzzleSortField>(PuzzleSortField.publishedAt, 'desc');

	// Data
	const [data, setData] = useState<ContentItemModel[]>(firstPage.items || []);
	const [totalDataCount, setTotalDataCount] = useState<number>(firstPage.count);
	const last = useRef<string | undefined>(firstPage.items[firstPage.items.length - 1]?.id);
	const [hasMore, setHasMore] = useState(firstPage.hasMore);
	const isInProgress = useRef<boolean>(false);

	// Handlers
	const fetchContent = useCallback(
		async (hardReset?: boolean) => {
			if (isInProgress.current || !creatorId) {
				return;
			}
			isInProgress.current = true;

			const lastId = !hardReset ? last.current : undefined;

			try {
				const res = await creatorService.getContentByCreator(creatorId, {
					order: sort,
					last: lastId,
				});

				setData((prevState) => {
					if (hardReset) {
						return [...res.items];
					}

					return [...prevState].concat([...res.items]);
				});
				setTotalDataCount(res.count);

				if (res.items.length) {
					last.current = res.items[res.items.length - 1].id;
				}
				setHasMore(res.hasMore);
			} catch (e) {
				enqueueSnackbar(t('screens.creator.errors.fetchDataError'), { variant: 'error' });
			} finally {
				isInProgress.current = false;
			}
		},
		[sort, creatorService, enqueueSnackbar, t, creatorId]
	);

	// Hooks
	useEffect(() => {
		// Fetch on init
		fetchContent(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sort, creatorId]);

	return {
		data,
		hasMore,
		handleSort,
		sort,
		fetchContent,
		isLoading: isInProgress.current,
		totalDataCount,
	};
};
